import { Link } from "react-router-dom";
import { ArrowRight, Star, Check } from "lucide-react";
import { Button } from "../ui/button";
import { SIGNUP_URL, planDisplayName, planPath } from "../../data/content";
import { useCurrency } from "../../contexts/CurrencyContext";
import { convertFromINR } from "../../data/currencies";
import AnimatedNumber from "../AnimatedNumber";

const HIGHLIGHT_WORDS_RE = /\b(unlimited|everything)\b/gi;

// What the platform renewal actually pays for - shown on the "Next Year"
// tab so the recurring fee reads as ongoing value, not just a repeat charge.
const RENEWAL_INCLUSIONS = [
  "Premium Support",
  "Software Updates",
  "Cloud Server & Hosting",
  "Security Patches",
  "New Feature Releases",
  "Uptime Monitoring",
];

function HighlightedText({ text }) {
  const parts = text.split(HIGHLIGHT_WORDS_RE);
  return parts.map((part, i) =>
    /^(unlimited|everything)$/i.test(part) ? (
      <strong key={i} className="text-[#FF6B00]">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const VIEW_META = {
  monthly: { unit: "/ MONTH", testId: "plan-monthly-fee" },
  quarterly: { unit: "/ QUARTER", testId: "plan-quarterly-fee" },
  yearly: { unit: "/ YEAR", testId: "plan-yearly-fee" },
  onetime: { unit: null, testId: "plan-onetime-fee" },
};

// `convert` lets a page swap in its own INR -> currency conversion.
// `showDetails` adds a button to the plan's own page (/pricing/:slug).
export default function PricingPlanCard({ plan, view = "monthly", convert = convertFromINR, showDetails = false }) {
  const { currency } = useCurrency();
  const money = (n) => `${currency.symbol}${Math.round(n).toLocaleString("en-US")}`;
  const isOneTime = view === "onetime";

  const tier =
    plan.pricing[view] || plan.pricing.onetime || plan.pricing.yearly || Object.values(plan.pricing)[0];
  const meta = VIEW_META[view] || VIEW_META.onetime;
  const amount = convert(tier.amount, currency);
  const setupAmount = tier.setup ? convert(tier.setup, currency) : 0;

  // Displayed as a limited-time 50% off deal: the struck-through "was" price
  // is always double the current amount, so savings == the amount itself.
  const price = { amount, testId: meta.testId, unit: meta.unit };
  const oldAmount = price.amount * 2;

  return (
    <div
      data-testid="pricing-plan-card"
      className={`relative flex-1 flex flex-col overflow-hidden rounded-xl border transition-all duration-300 hover:-translate-y-1.5 ${
        plan.badge
          ? "border-[#FF6B00] bg-gradient-to-b from-[#FFF3E9] to-white mb-card-shadow-hover lg:origin-top lg:scale-[1.03]"
          : "border-[#E6E9F1] bg-white mb-card-shadow hover:border-[#FF6B00]/40"
      }`}
    >
      <div className={`flex items-center justify-center gap-1.5 py-2.5 ${plan.badge ? "bg-[#FF6B00]" : ""}`}>
        {plan.badge ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-white text-white" />
            ))}
            <span className="text-xs font-bold tracking-wide text-white">{plan.badge}</span>
          </>
        ) : (
          <span className="text-xs">&nbsp;</span>
        )}
      </div>

      <div className="flex flex-1 flex-col px-8 pb-8 pt-6">
        <h3 className="text-xl font-bold text-[#0B1533]">{plan.name}</h3>
        {plan.channelAllowance && (
          <div className="mt-2 inline-flex w-fit items-center rounded-full bg-[#0B1533]/5 px-3 py-1 text-xs font-bold text-[#0B1533]">
            {plan.channelAllowance}
          </div>
        )}
        <p className="mt-2 text-sm text-[#475569] min-h-[40px]">
          <HighlightedText text={plan.tagline} />
        </p>

        <div className="mt-6 min-h-[110px] flex flex-col justify-center rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#94A3B8] line-through">{money(oldAmount)}</span>
            <span className="rounded-full bg-[#FF6B00] px-2 py-0.5 text-[10px] font-bold tracking-wide text-white">
              50% OFF
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <AnimatedNumber
              data-testid={price.testId}
              value={price.amount}
              format={money}
              className="text-4xl font-extrabold text-[#0B1533] tracking-tight"
            />
            {price.unit && <span className="text-xs font-semibold text-[#94A3B8]">{price.unit}</span>}
          </div>
          <div className="mt-2 inline-flex w-fit items-center rounded-md bg-[#FFF4D6] px-2.5 py-1">
            <span className="text-xs font-bold text-[#8A5A00]">
              You save {money(price.amount)} · Limited time
            </span>
          </div>
        </div>

        {setupAmount > 0 && (
          <div
            data-testid="plan-setup-fee"
            className="mt-3 flex items-center justify-between rounded-lg border border-dashed border-[#E6E9F1] bg-white px-4 py-2.5 text-sm"
          >
            <span className="font-medium text-[#475569]">One-time setup</span>
            <span className="font-bold text-[#0B1533]">+ {money(setupAmount)}</span>
          </div>
        )}

        {plan.cardFeatures ? (
          <div className="mt-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
              What's included
            </div>
            <div className="mt-2 grid gap-1.5">
              {plan.cardFeatures.map((item) => (
                <div key={item} className="flex items-start gap-1.5 text-xs text-[#475569]">
                  <Check className="w-3 h-3 mt-0.5 text-[#FF6B00] shrink-0" strokeWidth={3} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        ) : (
          !isOneTime && (
            <div className="mt-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                Your renewal covers
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                {RENEWAL_INCLUSIONS.map((item) => (
                  <div key={item} className="flex items-center gap-1.5 text-xs text-[#475569]">
                    <Check className="w-3 h-3 text-[#FF6B00] shrink-0" strokeWidth={3} />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        <Button
          data-testid="plan-cta"
          className={`mt-6 w-full font-semibold rounded-full h-11 hover:-translate-y-0.5 ${
            plan.badge
              ? "bg-[#FF6B00] hover:bg-[#FF8533] text-white hover:shadow-[0_10px_24px_rgba(255,107,0,0.35)]"
              : "bg-[#0B1533] hover:bg-[#1a2547] text-white"
          }`}
          asChild
        >
          <a href={SIGNUP_URL}>
            {plan.cta} <ArrowRight className="ml-1.5 w-4 h-4" />
          </a>
        </Button>
        {showDetails && plan.slug && (
          <Button
            data-testid="plan-details-link"
            variant="outline"
            className="mt-2.5 h-11 w-full rounded-full border-[#FF6B00] bg-white font-semibold text-[#FF6B00] hover:-translate-y-0.5 hover:bg-[#FFF3E9] hover:text-[#FF6B00]"
            asChild
          >
            <Link to={planPath(plan)}>
              {planDisplayName(plan.name)} Plan Details <ArrowRight className="ml-1.5 w-4 h-4" />
            </Link>
          </Button>
        )}
        <p className="mt-2 text-xs text-center text-[#94A3B8]">{plan.microcopy}</p>
      </div>
    </div>
  );
}
